import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Typography, Card } from 'antd';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  CbDeliverableItemV2Status,
  DeliverableReviewAction,
} from 'api/graphql-global-types';
// Components
import {
  ALREADY_REVIEWED_STATUSES,
  DeliverableReviewData,
} from '../../TimelineDeliverablesReviewForm';
import ReviewButtons from '../ReviewButtons/ReviewButtons';
// Styles
import styles from './FinalEditReviewSection.module.scss';

const { Text, Title } = Typography;

type FinalEditReviewSectionProps = {
  finalReelItem: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items;
  onApproveAll: () => void;
  onUnapprove: () => void;
  onRejectClick: (
    item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
  ) => void;
};

const VIDEO_EXTENSIONS = /\.(mp4|mov|webm|avi|mkv)$/i;

const isFilesItem = (
  item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): item is GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2 =>
  item.__typename === 'CbDeliverableFilesItemV2';

const isFileItem = (
  item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): item is GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2 =>
  item.__typename === 'CbDeliverableFileItemV2';

const getMediaUrl = (
  item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): string | undefined => {
  if (isFilesItem(item)) {
    return item.files?.[0]?.url || undefined;
  }
  if (isFileItem(item)) {
    return item.url || undefined;
  }
  return undefined;
};

const getMediaName = (
  item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): string => {
  if (isFilesItem(item)) {
    return item.files?.[0]?.name || 'Final Edit';
  }
  if (isFileItem(item)) {
    return item.name || 'Final Edit';
  }
  return 'Final Edit';
};

const isVideo = (url: string | undefined, name: string): boolean => {
  if (url && VIDEO_EXTENSIONS.test(url)) return true;
  if (VIDEO_EXTENSIONS.test(name)) return true;
  return false;
};

const FinalEditReviewSection: React.FC<FinalEditReviewSectionProps> = ({
  finalReelItem,
  onApproveAll,
  onUnapprove,
  onRejectClick,
}) => {
  const methods = useFormContext<DeliverableReviewData>();

  const finalReelReview = methods.watch('finalReelReview');
  const alreadyReviewed = ALREADY_REVIEWED_STATUSES.includes(
    finalReelItem.status
  );

  const isApproved =
    finalReelReview?.action === DeliverableReviewAction.APPROVE;
  const isRejected = finalReelReview?.action === DeliverableReviewAction.REJECT;

  const mediaUrl = getMediaUrl(finalReelItem);
  const mediaName = getMediaName(finalReelItem);
  const mediaIsVideo = isVideo(mediaUrl, mediaName);

  const handleApprove = () => {
    if (alreadyReviewed) return;

    if (isApproved) {
      // Toggle off - reset to null action
      methods.setValue('finalReelReview', {
        deliverableItemId: finalReelItem.id,
        action: null,
        feedback: '',
      });
      onUnapprove();
    } else {
      // Approve
      methods.setValue('finalReelReview', {
        deliverableItemId: finalReelItem.id,
        action: DeliverableReviewAction.APPROVE,
      });
      onApproveAll();
    }
  };

  const handleReject = () => {
    if (alreadyReviewed) return;

    methods.setValue('finalReelReview', {
      deliverableItemId: finalReelItem.id,
      action: DeliverableReviewAction.REJECT,
    });
  };

  const shouldShow =
    finalReelItem.status !== CbDeliverableItemV2Status.NOT_SUBMITTED;

  if (!shouldShow) return null;

  return (
    <div className={styles.finalEditSection}>
      <div className={styles.sectionHeader}>
        <Title level={4}>Final Edit</Title>
        <Text type="secondary">Review the final edited content</Text>
      </div>

      <Card className={styles.finalEditCard}>
        <div className={styles.mediaHeader}>
          <Title level={5} className={styles.mediaTitle}>
            {finalReelItem.task?.title || 'Final Edit'}
          </Title>
          <Text type="secondary" className={styles.statusText}>
            Status: {finalReelItem.status}
          </Text>
        </div>

        <div className={styles.mediaContainer}>
          {mediaUrl ? (
            <div className={styles.previewWrapper}>
              <p className={styles.itemLabel}>{mediaName}</p>
              {mediaIsVideo ? (
                <video
                  src={mediaUrl}
                  controls
                  className={styles.preview}
                  crossOrigin="anonymous"
                >
                  <track kind="captions" srcLang="en" label="English" />
                  <source src={mediaUrl} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={mediaUrl}
                  alt={mediaName}
                  className={styles.preview}
                />
              )}
            </div>
          ) : (
            <div className={styles.noContent}>
              <Text type="secondary">No media available</Text>
            </div>
          )}
        </div>

        <div className={styles.actionButtons}>
          <ReviewButtons
            isApproved={isApproved}
            isRejected={isRejected}
            handleApprove={handleApprove}
            handleReject={() => {
              handleReject();
              if (!isRejected) {
                onRejectClick(finalReelItem);
              }
            }}
            alreadyReviewed={alreadyReviewed}
            item={finalReelItem}
          />
        </div>
      </Card>
    </div>
  );
};

export default FinalEditReviewSection;
